import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import Utils, { downloadsFolder } from '../../utils/index';
import DownloadManagerUtils from '../../utils/downloadManager';
import NativeApi from '../../utils/nativeApi';
import UserAgentUtils from '../../utils/userAgent';
import {
	clearComponentLocalFileMissing,
	isComponentLocalFileMissing,
	markComponentLocalFileMissing,
} from '../../utils/componentLocalFileCache';
import StaticPage from '../../components/StaticPage';
import { GalleryInfosState } from '../Gallery/reducer';
import { DownloadManagerState } from '../DownloadManager/reducer';
import { OfflineManagerState, DownloadStatus } from '../OfflineManager/reducer';

const getOfflineFlags = (
	downloadManager: DownloadManagerState,
	offlineManager: OfflineManagerState,
	componentId: number,
) => {
	const isDownloadedInManager = DownloadManagerUtils.isItemDownloaded(downloadManager, componentId);
	const requiredItemStatus = offlineManager.requiredItems[componentId];
	const isDownloadedAsRequired = !!(requiredItemStatus && requiredItemStatus.status === DownloadStatus.DOWNLOADED);
	const isMarkedOffline = isDownloadedInManager || isDownloadedAsRequired;
	const needsStaleVerification = UserAgentUtils.isMobileApp
		&& isDownloadedAsRequired
		&& !isDownloadedInManager;

	return {
		isMarkedOffline,
		needsStaleVerification,
	};
};

const getIframeUrl = (
	galleryInfos: GalleryInfosState,
	componentId: number,
	locale: string,
	isMarkedOffline: boolean,
): string => {
	const useLocal = UserAgentUtils.isMobileApp
		&& isMarkedOffline
		&& !isComponentLocalFileMissing(componentId);
	return Utils.getComponentUrl(galleryInfos, componentId, useLocal, locale);
};

interface StaticPageContainerProps {
	galleryInfos: GalleryInfosState;
	downloadManager: DownloadManagerState;
	offlineManager: OfflineManagerState;
	locale: string;
	componentId: number;
	driveList: StoreState['driveList'];
	selection: StoreState['selection'];
	dispatch: Dispatch<StoreState>;
}

const StaticPageContainer = ({
	galleryInfos,
	downloadManager,
	offlineManager,
	locale,
	componentId,
	driveList,
	selection,
	dispatch,
}: StaticPageContainerProps) => {
	const { isMarkedOffline, needsStaleVerification } = getOfflineFlags(downloadManager, offlineManager, componentId);
	const iframeUrl = getIframeUrl(galleryInfos, componentId, locale, isMarkedOffline);
	const [verifiedIframeUrl, setVerifiedIframeUrl] = React.useState(iframeUrl);

	React.useEffect(() => {
		setVerifiedIframeUrl(iframeUrl);

		if (!needsStaleVerification || isComponentLocalFileMissing(componentId)) {
			return;
		}

		let cancelled = false;
		const localPath = `${downloadsFolder}/${componentId}/index.html`;

		NativeApi.existFile(localPath)
			.then((localExists) => {
				if (cancelled) {
					return;
				}

				if (localExists) {
					clearComponentLocalFileMissing(componentId);
					return;
				}

				markComponentLocalFileMissing(componentId);
				setVerifiedIframeUrl(Utils.getComponentUrl(galleryInfos, componentId, false, locale));
			})
			.catch(() => {
				if (!cancelled) {
					markComponentLocalFileMissing(componentId);
					setVerifiedIframeUrl(Utils.getComponentUrl(galleryInfos, componentId, false, locale));
				}
			});

		return () => {
			cancelled = true;
		};
	}, [componentId, locale, needsStaleVerification, iframeUrl, galleryInfos]);

	React.useEffect(() => {
		if (DownloadManagerUtils.isItemDownloaded(downloadManager, componentId)) {
			clearComponentLocalFileMissing(componentId);
		}
	}, [downloadManager, componentId]);

	return (
		<StaticPage
			downloadManager={downloadManager}
			driveList={driveList}
			selection={selection}
			componentId={componentId}
			iframeUrl={verifiedIframeUrl}
			dispatch={dispatch}
		/>
	);
};

const mapStateToProps = (state: StoreState, ownProps: { componentId: number }) => {
	const { galleryInfos, downloadManager, driveList, selection, i18n: { locale }, offlineManager } = state;
	const { componentId } = ownProps;

	return {
		galleryInfos,
		downloadManager,
		offlineManager,
		locale,
		componentId,
		driveList,
		selection,
	};
};

export default connect(mapStateToProps)(StaticPageContainer);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/StaticPage/index.tsx